import {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL
const CUSTOM_URL = `${API_URL}/Custom`

const buildDeviceInfo = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return ''
  }

  const timeZone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined

  return JSON.stringify({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen:
      window.screen && window.screen.width && window.screen.height
        ? `${window.screen.width}x${window.screen.height}`
        : undefined,
    timeZone,
  })
}

export const getExamRuleRequirement = async (examPeriodId, scope) => {
  if (!examPeriodId || !scope) {
    return {
      examPeriodId,
      requiresApproval: false,
      accepted: false,
      rules: [],
    }
  }

  const {data} = await axios.get(`${CUSTOM_URL}/GetMyExamRuleRequirement`, {
    params: {examPeriodId, scope},
  })
  return data
}

export const acceptExamRules = async (examPeriodId, scope) => {
  const {data} = await axios.post(`${CUSTOM_URL}/AcceptMyExamRules`, {
    examPeriodId,
    scope,
    deviceInfo: buildDeviceInfo(),
  })
  return data
}

export const useExamRulesApproval = (examPeriodId, scope) => {
  const [requirement, setRequirement] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState('')
  const [requirementKey, setRequirementKey] = useState('')

  const loadRequirement = useCallback(() => {
    if (!examPeriodId || !scope) {
      setRequirement(null)
      setRequirementKey('')
      setError('')
      setIsLoading(false)
      return Promise.resolve(null)
    }

    setIsLoading(true)
    setError('')

    return getExamRuleRequirement(examPeriodId, scope)
      .then((data) => {
        setRequirement(data)
        setRequirementKey(`${examPeriodId}:${scope}`)
        return data
      })
      .catch((err) => {
        console.error(err)
        const message =
          err?.response?.data?.message ||
          err?.response?.data ||
          'Sınav kuralları kontrol edilemedi. Lütfen tekrar deneyiniz.'
        setRequirement(null)
        setRequirementKey('')
        setError(String(message))
        return null
      })
      .finally(() => setIsLoading(false))
  }, [examPeriodId, scope])

  useEffect(() => {
    loadRequirement()
  }, [loadRequirement])

  const accept = useCallback(() => {
    if (!examPeriodId || !scope) return Promise.resolve(null)

    setIsAccepting(true)
    setError('')

    return acceptExamRules(examPeriodId, scope)
      .then((data) => {
        setRequirement(data)
        setRequirementKey(`${examPeriodId}:${scope}`)
        return data
      })
      .catch((err) => {
        console.error(err)
        const message =
          err?.response?.data?.message ||
          err?.response?.data ||
          'Sınav kuralları onayı kaydedilemedi. Lütfen tekrar deneyiniz.'
        setError(String(message))
        return null
      })
      .finally(() => setIsAccepting(false))
  }, [examPeriodId, scope])

  const currentRequirementKey = examPeriodId && scope ? `${examPeriodId}:${scope}` : ''
  const requirementMatches = !currentRequirementKey || requirementKey === currentRequirementKey
  const isPending = !!examPeriodId && !!scope && !requirementMatches && !error
  const isBlocked = requirementMatches && !!requirement?.requiresApproval
  const canView = !isLoading && !isPending && !error && !isBlocked

  return useMemo(
    () => ({
      requirement,
      isLoading,
      isAccepting,
      isPending,
      error,
      isBlocked,
      canView,
      reload: loadRequirement,
      accept,
      setRequirement,
    }),
    [accept, error, isAccepting, isBlocked, isLoading, isPending, loadRequirement, requirement, canView]
  )
}

export const ExamRulesApprovalModal = ({
  requirement,
  error,
  isAccepting,
  onAccept,
  onRetry,
  title = 'Sınav Kuralları Onayı',
}) => {
  if (!requirement?.requiresApproval && !error) return null

  const rules = requirement?.rules || []

  return (
    <>
      <div className='modal fade show d-block exam-rules-modal' tabIndex='-1' role='dialog' aria-modal='true'>
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>{title}</h5>
            </div>
            <div className='modal-body'>
              {error ? (
                <div className='alert alert-danger mb-0' role='alert'>
                  {error}
                </div>
              ) : (
                <>
                  <div
                    className='exam-rules-modal-body'
                    style={{
                      maxHeight: '58vh',
                      overflowY: 'auto',
                      padding: '16px',
                      border: '1px solid #e4e6ef',
                      borderRadius: '8px',
                      background: '#f9fafb',
                    }}
                  >
                    {rules.map((rule) => (
                      <section key={rule.participantRole} className='mb-6'>
                        <h5 className='fw-bold mb-3'>{rule.participantRoleLabel}</h5>
                        <div dangerouslySetInnerHTML={{__html: rule.rulesHtml || ''}} />
                      </section>
                    ))}
                  </div>
                  <div className='text-muted small mt-3'>
                    Sınav bilgilerini ve görevlerinizi görüntülemek için ilgili dönem kurallarını okuyup onaylamanız gerekmektedir.
                  </div>
                </>
              )}
            </div>
            <div className='modal-footer'>
              {error && (
                <button type='button' className='btn btn-light' onClick={onRetry}>
                  Tekrar dene
                </button>
              )}
              <button
                type='button'
                className='btn btn-primary'
                onClick={onAccept}
                disabled={!!error || isAccepting}
              >
                {isAccepting && <span className='spinner-border spinner-border-sm align-middle me-2'></span>}
                Okudum, anladım ve onaylıyorum
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' />
    </>
  )
}
